/**
 * The release notes the app ships. The list comes from `src/releases/index.json`, the front matter of
 * every release document (version, date, highlight; newest first), generated by
 * scripts/sync-releases.mjs. The bodies are the documents themselves (`src/releases/v<version>.md`),
 * one lazy import each: the list never loads a body, and a body loads only when its release is shown.
 */

import index from '@/releases/index.json';

export interface Release {
  version: string;
  /** Calendar day of the release, `YYYY-MM-DD`; empty when the document has none. */
  date: string;
  /** One line saying what the release is about. */
  highlight: string;
}

export const RELEASES: readonly Release[] = index;

/** The release with this version: an exact match in the bundled index, never a path or a pattern. */
export function releaseFor(version: string | undefined, releases: readonly Release[] = RELEASES): Release | undefined {
  return releases.find((release) => release.version === version);
}

const LOADERS = import.meta.glob<string>('../../releases/v*.md', { query: '?raw', import: 'default' });
const FRONT_MATTER = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/;
const LEADING_TITLE = /^\s*# .*\r?\n?/;

/** A release document without its front matter and the `# Version x` title the card shows itself. */
export function bodyOf(raw: string): string {
  return raw.replace(FRONT_MATTER, '').replace(LEADING_TITLE, '').trim();
}

/** The notes of a release from the index; empty when the bundle holds no document for it. */
export async function loadBody(version: string): Promise<string> {
  const load = LOADERS[`../../releases/v${version}.md`];
  return load ? bodyOf(await load()) : '';
}

/** Releases whose version or highlight contains the text, case-insensitively; blank text keeps all. */
export function matching(releases: readonly Release[], text: string): Release[] {
  const needle = text.trim().toLowerCase();
  if (!needle) return [...releases];
  return releases.filter((release) => `${release.version} ${release.highlight}`.toLowerCase().includes(needle));
}

/** Consecutive releases of one year, in the order given (the index is newest first). */
export function byYear(releases: readonly Release[]): { year: string; releases: Release[] }[] {
  const groups: { year: string; releases: Release[] }[] = [];
  for (const release of releases) {
    const year = release.date.slice(0, 4) || 'Undated';
    const last = groups[groups.length - 1];
    if (last?.year === year) last.releases.push(release);
    else groups.push({ year, releases: [release] });
  }
  return groups;
}
